/**
 * CMS SDK - useTags Hook
 * 标签管理 Hook
 */

import { useState, useCallback, useEffect } from 'react';
import { useCMS } from '../context/CMSContext';

/**
 * 标签管理 Hook
 * @param {boolean} autoLoad - 是否自动加载
 * @returns {Object} 标签操作对象
 */
export function useTags(autoLoad = true) {
  const { api } = useCMS();
  const [tags, setTags] = useState([]);
  const [hotTags, setHotTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * 加载标签列表
   */
  const load = useCallback(async (params = {}) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getTags(params);
      const items = result.items || result.data || result || [];
      setTags(items);
      return items;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api]);

  /**
   * 加载热门标签
   */
  const loadHotTags = useCallback(async (limit = 20) => {
    try {
      const result = await api.getHotTags(limit);
      const items = result.items || result.data || result || [];
      setHotTags(items);
      return items;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [api]);

  /**
   * 搜索标签
   */
  const search = useCallback(async (keyword) => {
    return load({ keyword });
  }, [load]);

  /**
   * 创建标签
   */
  const create = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.createTag(data);
      setTags(prev => [...prev, result]);
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api]);

  /**
   * 更新标签
   */
  const update = useCallback(async (tagId, data) => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.updateTag(tagId, data);
      setTags(prev => prev.map(t => t.id === tagId ? result : t));
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api]);

  /**
   * 删除标签
   */
  const remove = useCallback(async (tagId) => {
    setLoading(true);
    setError(null);
    try {
      await api.deleteTag(tagId);
      setTags(prev => prev.filter(t => t.id !== tagId));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [api]);

  /**
   * 根据 ID 获取标签
   */
  const getById = useCallback((tagId) => {
    return tags.find(t => t.id === tagId);
  }, [tags]);

  /**
   * 根据名称获取标签
   */
  const getByName = useCallback((name) => {
    return tags.find(t => t.name === name);
  }, [tags]);

  // 自动加载
  useEffect(() => {
    if (autoLoad) {
      load();
      loadHotTags();
    }
  }, [autoLoad, load, loadHotTags]);

  return {
    tags,
    hotTags,
    loading,
    error,
    load,
    loadHotTags,
    search,
    create,
    update,
    remove,
    getById,
    getByName,
  };
}

export default useTags;
